import React, { useState } from "react";
import { CellContext } from "@tanstack/react-table";
import { Interview } from "@/services/interviewService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Wand2, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { aiService } from "@/services/serviceFactory";
import { toast } from "@/hooks/use-toast";

interface AnonymizedAttributionCellProps
  extends CellContext<Interview, unknown> {}

export const AnonymizedAttributionCell: React.FC<
  AnonymizedAttributionCellProps
> = ({ getValue, row, column, table }) => {
  const initialValue = getValue() as string;
  const [value, setValue] = useState(initialValue || "");
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Get the custom data from the table meta
  const meta = table.options.meta as any;

  React.useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleGenerateAttribution = async () => {
    const interview = row.original;

    if (!interview.title || !interview.company) {
      toast({
        title: "Missing Information",
        description:
          "Title and company are required to generate anonymized attribution.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const anonymizedAttribution =
        await aiService.generateAnonymizedAttribution(
          interview.title,
          interview.company
        );

      if (anonymizedAttribution) {
        setValue(anonymizedAttribution);

        // Update the table data
        if (meta?.updateData) {
          meta.updateData(
            interview.id,
            "anonymizedAttribution",
            anonymizedAttribution
          );
        }

        toast({
          title: "Success",
          description: "Anonymized attribution generated successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate anonymized attribution.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating anonymized attribution:", error);
      toast({
        title: "Error",
        description: "An error occurred while generating the attribution.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = () => {
    if (meta?.updateData) {
      meta.updateData(row.original.id, "anonymizedAttribution", value);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Save the current edit
      handleSave();

      // Prevent default Enter behavior to avoid form submission
      e.preventDefault();

      // Get all visible rows
      const rows = table.getRowModel().rows;
      const currentRowIndex = rows.findIndex((r) => r.id === row.id);
      const nextRowIndex = currentRowIndex + 1;

      // If there's a next row, focus the same column in the next row
      if (nextRowIndex < rows.length) {
        const nextRowId = rows[nextRowIndex].original.id;

        setTimeout(() => {
          // Use document query to find the cell directly in the DOM
          const targetCell = document.querySelector(
            `[data-cell-id="${nextRowId}:anonymizedAttribution"]`
          );
          if (targetCell) {
            (targetCell as HTMLElement).click();
          }
        }, 50);
      }
    } else if (e.key === "Escape") {
      setValue(initialValue || "");
      setIsEditing(false);
    } else if (e.key === "Tab") {
      // Prevent default tab behavior
      e.preventDefault();

      // Save the current edit
      handleSave();

      // Get all visible columns that can be edited (excluding the select column and non-editable columns)
      const visibleColumns = table
        .getVisibleLeafColumns()
        .filter((col) => col.id !== "select" && col.id !== "totalCost");

      // Find the index of the current column
      const currentColIndex = visibleColumns.findIndex(
        (col) => col.id === "anonymizedAttribution"
      );

      // Determine the next column based on shift key
      const nextColIndex = e.shiftKey
        ? currentColIndex - 1
        : currentColIndex + 1;

      // Target information for next cell
      let targetRowId = row.original.id;
      let targetColId = "";

      // Handle going to the next row if we're at the end of columns
      if (nextColIndex >= visibleColumns.length && !e.shiftKey) {
        const rows = table.getRowModel().rows;
        const currentRowIndex = rows.findIndex((r) => r.id === row.id);
        const nextRowIndex = currentRowIndex + 1;

        if (nextRowIndex < rows.length) {
          // Go to first column of next row
          targetRowId = rows[nextRowIndex].original.id;
          targetColId = visibleColumns[0].id;
        } else {
          // Stay in the current cell if we're at the last cell of the table
          targetRowId = row.original.id;
          targetColId = "anonymizedAttribution";
        }
      }
      // Handle going to previous row if we're at the beginning of columns
      else if (nextColIndex < 0 && e.shiftKey) {
        const rows = table.getRowModel().rows;
        const currentRowIndex = rows.findIndex((r) => r.id === row.id);
        const prevRowIndex = currentRowIndex - 1;

        if (prevRowIndex >= 0) {
          // Go to last column of previous row
          targetRowId = rows[prevRowIndex].original.id;
          targetColId = visibleColumns[visibleColumns.length - 1].id;
        } else {
          // Stay in the current cell if we're at the first cell of the table
          targetRowId = row.original.id;
          targetColId = "anonymizedAttribution";
        }
      }
      // Standard next/prev column in same row
      else if (nextColIndex >= 0 && nextColIndex < visibleColumns.length) {
        targetColId = visibleColumns[nextColIndex].id;
      } else {
        // No valid next cell
        targetRowId = row.original.id;
        targetColId = "anonymizedAttribution";
      }

      // Use setTimeout to ensure the current edit is completely finished
      setTimeout(() => {
        const targetCell = document.querySelector(
          `[data-cell-id="${targetRowId}:${targetColId}"]`
        );
        if (targetCell) {
          (targetCell as HTMLElement).click();
        }
      }, 50);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className="flex-1 h-8 focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder="Enter anonymized attribution..."
        />
      </div>
    );
  }

  return (
    <div
      className="relative flex items-center w-full min-h-[32px] group"
      data-cell-id={`${row.original.id}:anonymizedAttribution`}
      onClick={() => setIsEditing(true)}
    >
      <div className="flex-1 cursor-pointer flex items-center truncate pr-1">
        {value || (
          <span className="text-gray-400 dark:text-gray-500 italic">
            Auto-generated during sync or click to edit
          </span>
        )}
      </div>

      {/* Overlay button on hover with gradient background */}
      <div className="absolute right-0 top-0 bottom-0 flex items-center opacity-0 group-hover:opacity-100 transition-all duration-200 bg-gradient-to-l from-gray-50/90 dark:from-gray-700/90 via-gray-50/60 dark:via-gray-700/60 to-transparent pl-4 pr-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleGenerateAttribution();
          }}
          disabled={isGenerating}
          className="h-7 w-7 p-0 hover:bg-gray-200 dark:hover:bg-gray-600 shadow-sm bg-gray-100 dark:bg-gray-700"
          title="Generate anonymized attribution using AI"
        >
          {isGenerating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-600 dark:text-purple-400" />
          ) : (
            <Wand2 className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
          )}
        </Button>
      </div>
    </div>
  );
};
